import { useEffect, useState } from 'react';
import { Api } from '../../services/api';
import { normalizeCategories as normalizeCategoryPayload } from '../home/CollectionsSection';
import ProductsSection from '../home/ProductsSection';
import { useLanguage } from '../../context/LanguageContext';

const pickBannerWithImage = (payload) => {
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
    ? payload.data
    : [];

  return source.find((banner) => Boolean(banner?.image)) || null;
};

const getRedirectUrl = (banner) => {
  if (!banner) {
    return '';
  }
  return banner.redirectUrl ?? banner.redirect_url ?? banner.url ?? '';
};

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [status, setStatus] = useState('idle');
  const [heroBanner, setHeroBanner] = useState(null);
  const { t } = useLanguage();

  useEffect(() => {
    let mounted = true;
    setStatus('loading');

    Api.fetchCategories()
      .then((data) => {
        if (!mounted) return;
        const normalized = normalizeCategoryPayload(data);
        setCategories(normalized);
        setStatus('success');
      })
      .catch(() => {
        if (!mounted) return;
        setCategories([]);
        setStatus('error');
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    Api.fetchCategoriesHeroBanner()
      .then((data) => {
        if (!mounted) return;
        setHeroBanner(pickBannerWithImage(data));
      })
      .catch(() => {
        if (!mounted) return;
        setHeroBanner(null);
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Every category (hidden ones are already left out, sorted by priority, by normalizeCategories)
  const visibleCategories = categories;
  const heroImage = heroBanner?.image;
  const heroAlt =
    heroBanner?.title || heroBanner?.description || 'Carry style everywhere';
  const heroRedirectUrl = getRedirectUrl(heroBanner);

  const navigateTo = (url) => {
    if (!url) return;
    if (typeof window !== 'undefined') {
      window.location.assign(url);
    }
  };

  const handleKeyPress = (event, url) => {
    if (!url) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigateTo(url);
    }
  };

  return (
    <div className="categories-page">
      <section
        className="categories-hero"
        role={heroRedirectUrl ? 'button' : undefined}
        tabIndex={heroRedirectUrl ? 0 : undefined}
        onClick={heroRedirectUrl ? () => navigateTo(heroRedirectUrl) : undefined}
        onKeyPress={
          heroRedirectUrl
            ? (event) => handleKeyPress(event, heroRedirectUrl)
            : undefined
        }
      >
        <img
          src={heroImage}
          alt={heroAlt}
          loading="lazy"
          onError={(event) => {
          }}
        />
       
      </section>

      <section className="categories-gallery" id="categories">
      

        {status === 'error' && (
          <p className="categories-gallery__state">Unable to load categories right now.</p>
        )}

        {status === 'success' && visibleCategories.length === 0 && (
          <p className="categories-gallery__state">No categories available right now.</p>
        )}

        {status === 'success' && visibleCategories.length > 0 && (
          <div className="categories-gallery__grid">
            {visibleCategories.map((category) => (
              <article
                key={category.id}
                className="categories-card"
                onClick={() => {
                  if (typeof window !== 'undefined') {
                    window.location.assign(
                      `/products?category=${encodeURIComponent(category.id)}`
                    );
                  }
                }}
                role="button"
                tabIndex={0}
                onKeyPress={(event) => {
                  if (event.key === 'Enter') {
                    if (typeof window !== 'undefined') {
                      window.location.assign(
                        `/products?category=${encodeURIComponent(category.id)}`
                      );
                    }
                  }
                }}
              >
                <div className="categories-card__image">
                  <img
                    src={category.image}
                    alt={category.name}
                    loading="lazy"
                    onError={(event) => {
                    }}
                  />
                </div>
                <p>{category.name}</p>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="categories-recommendations">
        <ProductsSection title={t('products.you_might_like')} variant="compact" limit={5} />
      </section>
    </div>
  );
};

export default CategoriesPage;
